import { Head } from '@inertiajs/react';
import { m, useScroll, useTransform } from 'framer-motion';
import {
    ShieldCheck,
    MapPinned,
    TimerReset,
    Smartphone,
    BarChart3,
    ArrowRight,
    Lock,
} from 'lucide-react';
import { useRef } from 'react';
import LandingFooter from '@/components/landing-footer';
import LandingHero from '@/components/landing-hero';
import LandingNav from '@/components/landing-nav';
import { easing, fadeUp, stagger } from '@/lib/landing-motion';
import { register, bantuan } from '@/routes';

interface Feature {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
}

const features: Feature[] = [
    {
        icon: MapPinned,
        title: 'Geofencing presisi',
        description:
            'Verifikasi lokasi berbasis peta dengan radius presisi meter, langsung dari GPS perangkat santri.',
    },
    {
        icon: TimerReset,
        title: 'Pemantauan real-time',
        description:
            'Admin memantau kehadiran secara langsung dari dasbor, kapan pun dan di mana pun.',
    },
    {
        icon: ShieldCheck,
        title: 'Enkripsi AES-256',
        description:
            'Koordinat lokasi dan foto wajah dilindungi enkripsi tingkat tinggi demi privasi santri.',
    },
];

const howItWorks = [
    {
        icon: Smartphone,
        title: 'Buka aplikasi',
        description: 'Santri membuka aplikasi presensi dari smartphone.',
    },
    {
        icon: MapPinned,
        title: 'Verifikasi lokasi',
        description: 'Sistem mendeteksi posisi GPS di dalam zona geofence.',
    },
    {
        icon: Lock,
        title: 'Enkripsi & simpan',
        description: 'Data dienkripsi AES-256 sebelum tersimpan di server.',
    },
    {
        icon: BarChart3,
        title: 'Laporan lengkap',
        description: 'Admin memperoleh rekap kehadiran untuk tindak lanjut.',
    },
];

function Eyebrow({ children }: { children: React.ReactNode }) {
    return (
        <m.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-primary uppercase"
        >
            <span className="h-px w-6 bg-primary/50" />
            {children}
        </m.span>
    );
}

export default function Welcome({
    nama_instansi,
    alamat_instansi,
    site_logo,
}: {
    nama_instansi: string;
    alamat_instansi: string;
    site_logo?: string;
}) {
    // Scroll-scrubbed beam for the "How It Works" steps (scrollytelling).
    const stepsRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress: stepsProgress } = useScroll({
        target: stepsRef,
        offset: ['start 80%', 'end 60%'],
    });
    const beamScaleX = useTransform(stepsProgress, [0, 1], [0, 1]);

    return (
        <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-white">
            <Head title={`${nama_instansi} - Presensi Digital`} />

            <LandingNav name={nama_instansi} logo={site_logo} />

            <main>
                <LandingHero name={nama_instansi} logo={site_logo} />

                {/* ── ABOUT + FEATURES ─────────────────────────────────────── */}
                <section
                    id="about"
                    className="scroll-mt-20 border-t border-border/60 px-5 py-24 lg:px-8"
                >
                    <div className="mx-auto max-w-6xl">
                        <m.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-80px' }}
                            className="max-w-2xl"
                        >
                            <Eyebrow>Modernitas dalam tradisi</Eyebrow>
                            <m.h2
                                variants={fadeUp}
                                className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                            >
                                Adab pesantren, dipadukan teknologi terkini.
                            </m.h2>
                            <m.p
                                variants={fadeUp}
                                className="mt-4 text-base leading-relaxed text-muted-foreground"
                            >
                                {nama_instansi} mencatat kehadiran santri secara
                                akurat, menumbuhkan kedisiplinan, dan menjaga
                                keamanan data secara real-time.
                            </m.p>
                        </m.div>

                        {/* Features grid */}
                        <m.div
                            id="features"
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-60px' }}
                            className="mt-14 grid scroll-mt-24 grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 md:grid-cols-3"
                        >
                            {features.map((feat) => (
                                <m.div
                                    key={feat.title}
                                    variants={fadeUp}
                                    className="group bg-background p-8 transition-colors hover:bg-muted/40"
                                >
                                    <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                                        <feat.icon className="size-5" />
                                    </div>
                                    <h3 className="mt-5 text-lg font-semibold tracking-tight text-foreground">
                                        {feat.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                        {feat.description}
                                    </p>
                                </m.div>
                            ))}
                        </m.div>
                    </div>
                </section>

                {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
                <section
                    id="guide"
                    className="scroll-mt-20 border-t border-border/60 bg-muted/30 px-5 py-24 lg:px-8"
                >
                    <div className="mx-auto max-w-6xl">
                        <m.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-80px' }}
                            className="max-w-2xl"
                        >
                            <Eyebrow>Cara kerja</Eyebrow>
                            <m.h2
                                variants={fadeUp}
                                className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                            >
                                Empat langkah, satu kali ketuk.
                            </m.h2>
                        </m.div>

                        <m.div
                            ref={stepsRef}
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-60px' }}
                            className="relative mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4"
                        >
                            {/* Scroll-scrubbed progress beam (desktop) */}
                            <div className="absolute top-6 left-[12%] hidden h-px w-[76%] overflow-hidden bg-border lg:block">
                                <m.div
                                    style={{ scaleX: beamScaleX }}
                                    className="h-full w-full origin-left bg-gradient-to-r from-primary via-secondary to-[#ffd709]"
                                />
                            </div>

                            {howItWorks.map((step, idx) => (
                                <m.div
                                    key={step.title}
                                    variants={fadeUp}
                                    className="relative"
                                >
                                    <div className="flex size-12 items-center justify-center rounded-full border border-border/60 bg-background text-primary">
                                        <step.icon className="size-5" />
                                    </div>
                                    <div className="mt-5 text-xs font-semibold tracking-widest text-muted-foreground">
                                        0{idx + 1}
                                    </div>
                                    <h3 className="mt-1.5 text-base font-semibold tracking-tight text-foreground">
                                        {step.title}
                                    </h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                                        {step.description}
                                    </p>
                                </m.div>
                            ))}
                        </m.div>
                    </div>
                </section>

                {/* ── SECURITY ─────────────────────────────────────────────── */}
                <section
                    id="security"
                    className="scroll-mt-20 border-t border-border/60 px-5 py-24 lg:px-8"
                >
                    <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-2 lg:items-center">
                        <m.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-80px' }}
                        >
                            <Eyebrow>Keamanan & privasi</Eyebrow>
                            <m.h2
                                variants={fadeUp}
                                className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
                            >
                                Privasi santri adalah amanah.
                            </m.h2>
                            <m.p
                                variants={fadeUp}
                                className="mt-4 text-base leading-relaxed text-muted-foreground"
                            >
                                Setiap titik lokasi dan identitas dilindungi
                                enkripsi modern, sehingga selalu aman dan
                                terjaga kerahasiaannya.
                            </m.p>

                            <m.div variants={fadeUp} className="mt-8 space-y-4">
                                {[
                                    {
                                        icon: MapPinned,
                                        title: 'Lokasi terenkripsi',
                                        desc: 'Koordinat GPS dilindungi enkripsi AES-256.',
                                    },
                                    {
                                        icon: Smartphone,
                                        title: 'Biometrik aman',
                                        desc: 'Foto verifikasi disimpan terenkripsi penuh.',
                                    },
                                ].map(({ icon: Icon, title, desc }) => (
                                    <div
                                        key={title}
                                        className="flex gap-4 rounded-xl border border-border/60 bg-card/50 p-4"
                                    >
                                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                            <Icon className="size-5" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-foreground">
                                                {title}
                                            </div>
                                            <div className="mt-0.5 text-sm text-muted-foreground">
                                                {desc}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </m.div>
                        </m.div>

                        {/* Security visual */}
                        <m.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, ease: easing }}
                            className="relative flex justify-center"
                        >
                            <div className="relative flex size-72 items-center justify-center">
                                <m.div
                                    animate={{ rotate: 360 }}
                                    transition={{
                                        duration: 28,
                                        repeat: Infinity,
                                        ease: 'linear',
                                    }}
                                    className="absolute inset-0 rounded-full border border-dashed border-primary/20"
                                />
                                <div className="absolute inset-8 rounded-full border border-primary/10" />
                                <div className="relative flex size-44 flex-col items-center justify-center rounded-full border border-border/60 bg-card shadow-xl shadow-primary/5">
                                    <ShieldCheck className="size-16 text-primary" />
                                    <div className="mt-2 text-lg font-semibold tracking-tight text-foreground">
                                        AES-256
                                    </div>
                                    <div className="text-[10px] font-semibold tracking-[0.2em] text-primary uppercase">
                                        Encrypted
                                    </div>
                                </div>
                            </div>
                        </m.div>
                    </div>
                </section>

                {/* ── CTA ──────────────────────────────────────────────────── */}
                <section className="px-5 pb-24 lg:px-8">
                    <m.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.6, ease: easing }}
                        className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl border border-border/60 bg-primary px-6 py-16 text-center md:py-20"
                    >
                        <div className="bg-dot-grid pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)] text-white/10" />
                        <div className="relative">
                            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                                Siap mendigitalkan presensi {nama_instansi}?
                            </h2>
                            <p className="mx-auto mt-4 max-w-md text-base text-white/75">
                                Wujudkan presensi santri yang lebih disiplin,
                                aman, dan terpercaya. Mulai hari ini.
                            </p>
                            <div className="mt-8 flex flex-wrap justify-center gap-3">
                                <a
                                    href={register.url()}
                                    className="inline-flex h-11 items-center gap-1.5 rounded-full bg-white px-6 text-sm font-medium text-primary transition-colors hover:bg-white/90"
                                >
                                    Mulai gratis
                                    <ArrowRight className="size-4" />
                                </a>
                                <a
                                    href={bantuan.url()}
                                    className="inline-flex h-11 items-center rounded-full border border-white/30 px-6 text-sm font-medium text-white transition-colors hover:bg-white/10"
                                >
                                    Pelajari lebih lanjut
                                </a>
                            </div>
                        </div>
                    </m.div>
                </section>
            </main>

            <LandingFooter
                name={nama_instansi}
                address={alamat_instansi}
                logo={site_logo}
            />
        </div>
    );
}
